import { Link } from 'react-router-dom';
import SEOHelmet from '../components/SEOHelmet';
import { ALL_SERVICE_CARDS } from '../config/servicePagesData';

const SERVICE_STATS = [
  ['8', 'Core service lines'],
  ['24h', 'Proposal response'],
  ['4', 'Engagement models'],
  ['100%', 'Custom scope'],
];

const ENGAGEMENTS = [
  ['Fixed Scope', 'Best for defined websites, MVPs, dashboards, and integrations.'],
  ['Dedicated Team', 'Best for ongoing product development and roadmap delivery.'],
  ['Monthly Retainer', 'Best for support, maintenance, cloud operations, and improvements.'],
  ['White Label', 'Best for agencies that need reliable delivery under their own brand.'],
];

export default function ServicesPage() {
  return (
    <>
      <SEOHelmet
        title="All Services | AxentraLab"
        description="Explore AxentraLab software development, SaaS, MVP, cloud, IoT, modernization, support, and managed hosting services."
        keywords="software development services, SaaS development, MVP development, cloud computing, managed hosting"
      />
      <div style={{ background: 'var(--bg)', minHeight: '100vh', overflowX: 'hidden' }}>
        <style>{`
          @keyframes fadeUp { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: none; } }
          .services-shell { max-width: 1180px; margin: 0 auto; padding: 0 5%; }
          .services-label {
            display: inline-flex; align-items: center; gap: 10px; color: var(--teal);
            font-family: var(--font-m); font-size: 12px; font-weight: 800; letter-spacing: 1.5px;
            text-transform: uppercase; margin-bottom: 18px;
          }
          .services-label::before { content: ''; width: 28px; height: 2px; border-radius: 99px; background: var(--teal); }
          .stats-grid, .services-grid, .engagement-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
          .services-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); }
          .service-card {
            border: 1px solid var(--border); border-radius: 8px; background: #fff; padding: 24px;
            min-height: 320px; display: flex; flex-direction: column;
            transition: transform .2s ease, border-color .2s ease, box-shadow .2s ease;
          }
          .service-card:hover { transform: translateY(-5px); border-color: rgba(37,99,235,.3); box-shadow: 0 22px 48px -30px rgba(15,23,42,.48); }
          .outcome-pill {
            display: inline-flex; min-height: 28px; align-items: center; padding: 6px 10px; border-radius: 8px;
            border: 1px solid var(--border); background: #fff; color: var(--muted);
            font-family: var(--font-m); font-size: 11px; font-weight: 800;
          }
          @media (max-width: 1100px) { .services-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
          @media (max-width: 980px) { .stats-grid, .engagement-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } .cta-layout { grid-template-columns: 1fr !important; } }
          @media (max-width: 680px) { .stats-grid, .services-grid, .engagement-grid { grid-template-columns: 1fr; } .hero-title { font-size: 42px !important; } }
          @media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation: none !important; transition: none !important; } }
        `}</style>

        <section style={{ padding: '150px 0 76px', position: 'relative', overflow: 'hidden', background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)' }}>
          <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(var(--border) 1px,transparent 1px),linear-gradient(90deg,var(--border) 1px,transparent 1px)', backgroundSize: '64px 64px', opacity: 0.44 }} />
          <div className="services-shell" style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ maxWidth: 880, animation: 'fadeUp .7s ease both' }}>
              <div className="services-label">All Services</div>
              <h1 className="hero-title" style={{ fontFamily: 'var(--font-h)', fontSize: 'clamp(48px,7vw,86px)', lineHeight: .98, color: 'var(--text)', margin: '0 0 24px', fontWeight: 900 }}>
                Software services for products, platforms, cloud, and growth.
              </h1>
              <p style={{ color: 'var(--muted)', fontSize: 18, lineHeight: 1.75, maxWidth: 680, marginBottom: 34 }}>
                Choose a focused service or combine multiple workstreams into one delivery plan. Every engagement starts with clear scope, timeline, and ownership.
              </p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <Link to="/contact" className="btn-primary" style={{ padding: '15px 30px' }}>Get Free Estimation -&gt;</Link>
                <Link to="/portfolio" className="btn-outline" style={{ padding: '15px 28px' }}>View Work</Link>
              </div>
            </div>
            <div className="stats-grid" style={{ marginTop: 54 }}>
              {SERVICE_STATS.map(([value, label]) => (
                <div key={label} style={{ border: '1px solid var(--border)', borderRadius: 8, background: 'rgba(255,255,255,.82)', padding: '22px 20px' }}>
                  <div style={{ fontFamily: 'var(--font-h)', fontSize: 34, fontWeight: 900, color: 'var(--text)', lineHeight: 1 }}>{value}</div>
                  <div style={{ marginTop: 8, color: 'var(--muted)', fontFamily: 'var(--font-m)', fontSize: 12 }}>{label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="services-shell" style={{ padding: '76px 5% 92px' }}>
          <div style={{ marginBottom: 34 }}>
            <div className="services-label">Service Lines</div>
            <h2 style={{ fontFamily: 'var(--font-h)', fontSize: 'clamp(30px,4.5vw,52px)', lineHeight: 1.05, color: 'var(--text)', margin: 0, fontWeight: 900 }}>
              Pick the capability your business needs now.
            </h2>
          </div>
          <div className="services-grid">
            {ALL_SERVICE_CARDS.map((service) => (
              <Link key={service.eyebrow} to={service.route} className="service-card" style={{ textDecoration: 'none' }}>
                <div style={{ width: 46, height: 46, borderRadius: 8, display: 'grid', placeItems: 'center', background: `${service.accent}12`, color: service.accent, fontFamily: 'var(--font-m)', fontWeight: 900, marginBottom: 20 }}>
                  {service.eyebrow.split(' ').map((word) => word[0]).join('').slice(0, 2)}
                </div>
                <div style={{ color: service.accent, fontFamily: 'var(--font-m)', fontSize: 12, fontWeight: 800, textTransform: 'uppercase', marginBottom: 10 }}>{service.eyebrow}</div>
                <h3 style={{ fontFamily: 'var(--font-h)', color: 'var(--text)', fontSize: 22, lineHeight: 1.15, fontWeight: 900, margin: '0 0 14px' }}>{service.title}</h3>
                <p style={{ color: 'var(--muted)', fontSize: 14, lineHeight: 1.65, marginBottom: 18 }}>{service.summary}</p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 'auto', paddingTop: 18, borderTop: '1px solid var(--border)' }}>
                  <span className="outcome-pill">{service.timeline}</span>
                  <span className="outcome-pill">Details -&gt;</span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section style={{ padding: '82px 0', background: '#fff', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
          <div className="services-shell">
            <div style={{ textAlign: 'center', marginBottom: 42 }}>
              <div className="services-label" style={{ justifyContent: 'center' }}>Engagement Models</div>
              <h2 style={{ fontFamily: 'var(--font-h)', fontSize: 'clamp(30px,4.5vw,48px)', color: 'var(--text)', margin: 0, fontWeight: 900 }}>
                Work with us the way your project needs.
              </h2>
            </div>
            <div className="engagement-grid">
              {ENGAGEMENTS.map(([title, desc], index) => (
                <div key={title} className="service-card" style={{ minHeight: 0 }}>
                  <div style={{ color: 'var(--teal)', fontFamily: 'var(--font-h)', fontSize: 30, fontWeight: 900, marginBottom: 18 }}>{String(index + 1).padStart(2, '0')}</div>
                  <h3 style={{ color: 'var(--text)', fontFamily: 'var(--font-h)', fontSize: 20, fontWeight: 850, margin: '0 0 10px' }}>{title}</h3>
                  <p style={{ color: 'var(--muted)', fontSize: 14, lineHeight: 1.7 }}>{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="services-shell" style={{ padding: '94px 5% 110px' }}>
          <div className="cta-layout" style={{ border: '1px solid var(--border)', borderRadius: 8, background: '#0f172a', color: '#fff', padding: 'clamp(42px,7vw,76px)', display: 'grid', gridTemplateColumns: '1fr auto', gap: 28, alignItems: 'center' }}>
            <div>
              <div style={{ color: '#93c5fd', fontFamily: 'var(--font-m)', fontSize: 12, fontWeight: 800, textTransform: 'uppercase', marginBottom: 16 }}>Need help choosing?</div>
              <h2 style={{ fontFamily: 'var(--font-h)', fontSize: 'clamp(32px,5vw,58px)', lineHeight: 1.02, margin: '0 0 18px', fontWeight: 900 }}>
                Tell us your goal. We will recommend the right service mix.
              </h2>
              <p style={{ color: '#cbd5e1', fontSize: 16, lineHeight: 1.7, maxWidth: 620 }}>
                Share your project stage, deadline, and technical problems. We will map the practical next step.
              </p>
            </div>
            <Link to="/contact" className="btn-primary" style={{ padding: '15px 28px', background: '#fff', color: '#0f172a' }}>Talk to Us</Link>
          </div>
        </section>
      </div>
    </>
  );
}
